import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GetSurveyById, SaveSurveyDraft, PublishSurvey } from "../../../utils/apiCalls/surveyBuilder";
import { safeParseSessionStorage } from "../../../utils/storage";
import { useToast } from "../../../utils/ToastContext";
import { CHOICE_QUESTION_TYPES, STRUCTURAL_QUESTION_TYPES } from "../../../utils/surveysConstants";
import { downloadJSON } from "../../../utils/downloadFile";
import useConfirmDialog from "../../../hooks/useConfirmDialog";

const buildInitialValues = () => ({
  studyName: "",
  title: "",
  description: "",
  version: "1",
  clinicalPurpose: "",
  questions: [],
});

export default function useSurveyBuilder() {
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  // The list only hands over {id, ...list columns} — full survey content
  // (questions, study info) is fetched fresh from GetSurveyById below.
  const rowData = location?.state?.rowData || safeParseSessionStorage("surveyRowData");
  const surveyId = rowData?.id || null;
  const isEditing = !!surveyId;

  const [formData, setFormData] = useState(buildInitialValues);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(isEditing);
  const unsavedChangesDialog = useConfirmDialog();
  // Snapshot to diff formData against for the "unsaved changes" prompt —
  // starts as the blank form, then gets replaced with the fetched survey
  // once GetSurveyById resolves, so edits made *before* the fetch settles
  // don't wrongly appear dirty against stale empty values.
  const savedFormDataRef = useRef(buildInitialValues());

  useEffect(() => {
    if (!surveyId) return;
    setIsLoading(true);
    GetSurveyById(surveyId).then((res) => {
      if (res.statusCode === 200) {
        const survey = res.data;
        setFormData((prev) => {
          const next = {
            ...prev,
            studyName: survey.studyName,
            title: survey.title,
            description: survey.description,
            version: survey.version != null ? String(survey.version) : prev.version,
            clinicalPurpose: survey.clinicalPurpose,
            questions: survey.questions,
          };
          savedFormDataRef.current = next;
          return next;
        });
        setIsLoading(false);
      } else {
        // Confirmed against the real API: a stale/deleted surveyId comes
        // back as {statusCode: 400, message: "Not found"}. Nothing useful
        // to show here — bounce back to the list instead of leaving the
        // user stuck on a blank/broken builder.
        showToast(res.message || "Failed to load survey", "error");
        sessionStorage.removeItem("surveyRowData");
        navigate("/surveys");
      }
    });
  }, [surveyId, showToast, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Version is a whole number only — strip anything but digits (blocks
    // decimals, letters, etc.) as they're typed rather than validating after.
    const nextValue = name === "version" ? value.replace(/\D/g, "") : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleQuestionsChange = (questions) => {
    setFormData((prev) => ({ ...prev, questions }));
    setErrors((prev) => ({ ...prev, questions: "" }));
  };

  // Appends a new card from a clicked palette entry.
  const addQuestion = (paletteItem) => {
    const isStructural = STRUCTURAL_QUESTION_TYPES.includes(paletteItem.type);
    setFormData((prev) => {
      const newQuestion = {
        id: `Q-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        type: paletteItem.type,
        label: "",
        helpText: "",
        required: !isStructural,
        options: CHOICE_QUESTION_TYPES.includes(paletteItem.type) ? ["Option 1", "Option 2"] : [],
        scaleMin: paletteItem.defaults?.scaleMin,
        scaleMax: paletteItem.defaults?.scaleMax,
      };
      return { ...prev, questions: [...prev.questions, newQuestion] };
    });
  };

  // strict validation (question completeness) only applies when publishing
  // a live survey — a draft can be partial.
  const validateFields = (strict) => {
    const errObj = {};
    if (!formData.title.trim()) errObj.title = "Title is required";

    if (strict) {
      const realQuestions = formData.questions.filter((q) => !STRUCTURAL_QUESTION_TYPES.includes(q.type));
      if (realQuestions.length === 0) {
        errObj.questions = "Add at least one question";
      } else {
        const invalid = realQuestions.some(
          (q) =>
            !q.label?.trim() ||
            (CHOICE_QUESTION_TYPES.includes(q.type) && (q.options || []).filter(Boolean).length < 2),
        );
        if (invalid) errObj.questions = "Each question needs text; choice questions need at least 2 options";
      }
    }

    setErrors(errObj);
    return Object.values(errObj).every((x) => !x);
  };

  // mode: "draft" (save incomplete) or "publish" (save then publish).
  // Editing an already-published survey still goes through SaveSurveyDraft —
  // the API creates a new draft version automatically rather than overwriting
  // the live one, so the same two calls work for create, edit, and re-version.
  const handleSave = async (mode) => {
    if (!validateFields(mode !== "draft")) return;

    setIsLoading(true);
    try {
      const draftRes = await SaveSurveyDraft(surveyId, formData);
      if (draftRes.statusCode !== 200) {
        // The survey list (useSurveysList) already redirects "Edit" straight
        // to an existing Draft when one exists for the group, so this call
        // should only fail here on a race (e.g. a draft was created in
        // another tab between loading the list and saving here). Backstop:
        // send the user to the drafts list rather than a bare error toast.
        // Confirmed against the real API: message is exactly
        // "A draft version already exists for this survey".
        if ((draftRes.message || "").toLowerCase().includes("draft version already exists")) {
          showToast("A draft of this survey already exists — opening your drafts.", "info");
          sessionStorage.removeItem("surveyRowData");
          navigate("/surveys", { state: { statusFilter: "draft" } });
          return;
        }
        showToast(draftRes.message || "Failed to save survey", "error");
        return;
      }

      if (mode === "publish") {
        const draftId = draftRes.data?.surveyId ?? draftRes.data?.id ?? surveyId;
        const publishRes = await PublishSurvey(draftId);
        if (publishRes.statusCode !== 200) {
          showToast(publishRes.message || "Saved as draft, but publish failed", "error");
          return;
        }
        showToast("Survey published", "success");
      } else {
        showToast("Survey saved as draft", "success");
      }

      sessionStorage.removeItem("surveyRowData");
      navigate("/surveys");
    } catch (error) {
      showToast(error?.message || "Failed to save survey", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportJSON = () => {
    downloadJSON(`${formData.title || "survey"}-definition.json`, formData);
  };

  const leaveBuilder = () => {
    sessionStorage.removeItem("surveyRowData");
    navigate("/surveys");
  };

  // Back arrow and Cancel both route through here: if the form has drifted
  // from the last loaded/saved snapshot, confirm before discarding the edits
  // instead of navigating straight away.
  const handleCancel = () => {
    const isDirty = JSON.stringify(formData) !== JSON.stringify(savedFormDataRef.current);
    if (isDirty) {
      unsavedChangesDialog.openDialog();
    } else {
      leaveBuilder();
    }
  };

  return {
    formData,
    errors,
    isLoading,
    isEditing,
    handleChange,
    handleQuestionsChange,
    addQuestion,
    handleSave,
    handleExportJSON,
    handleCancel,
    unsavedChangesDialogOpen: unsavedChangesDialog.open,
    closeUnsavedChangesDialog: unsavedChangesDialog.closeDialog,
    confirmLeaveWithoutSaving: leaveBuilder,
  };
}
